import {
	FLIT_BIRD_RADIUS,
	FLIT_BIRD_X,
	FLIT_FIRST_PIPE,
	FLIT_GAP,
	FLIT_HEIGHT,
	FLIT_PIPE_SPACING,
	FLIT_PIPE_WIDTH,
	FLIT_WIDTH,
	type FlitInput,
	type FlitState,
	flitGap,
	flitPipeX,
} from "@playpal/core";
import { useEffect } from "react";

/** A share of the screen, as a percentage. */
const across = (units: number) => `${(units / FLIT_WIDTH) * 100}%`;
const down = (units: number) => `${(units / FLIT_HEIGHT) * 100}%`;

/** The world's width over its height — what the stage scales to fit. */
export const FLIT_ASPECT = FLIT_WIDTH / FLIT_HEIGHT;

interface Props {
	state: FlitState;
	onPress: (input: FlitInput) => void;
	/** False before the run starts and after it ends — taps stop working then. */
	live: boolean;
}

/**
 * The sky, the pipes, the bird — and the button.
 *
 * There is no separate control strip for this one: the whole board is the flap
 * button, because a one-button game with the button somewhere else is a game
 * played by looking away from it. It is also what every version of this a child
 * has seen does, so it is what they will try first.
 */
export function FlitField({ state, onPress, live }: Props) {
	useTapAnywhere(onPress, live);

	/*
	 * Only the pipes that could be on screen. A full run has five hundred and
	 * shows three.
	 *
	 * The offset of the first pipe belongs in this sum, and leaving it out does
	 * not draw too many — it draws none, for most of the run, because the index
	 * it lands on is several pipes further along than the one in view.
	 */
	const first = Math.max(
		0,
		Math.ceil((state.distance - FLIT_PIPE_WIDTH - FLIT_FIRST_PIPE) / FLIT_PIPE_SPACING),
	);
	const visible = [first, first + 1, first + 2, first + 3].filter((index) => {
		const left = flitPipeX(index, state.distance);
		return left > -FLIT_PIPE_WIDTH && left < FLIT_WIDTH;
	});

	return (
		<button
			type="button"
			aria-label="Flap"
			onPointerDown={(event) => {
				event.preventDefault();
				onPress({ flap: true });
			}}
			className="relative block w-full touch-none select-none overflow-hidden rounded-3xl bg-sky-300 shadow-inner ring-4 ring-sky-500/40 dark:bg-sky-800"
			style={{ aspectRatio: `${FLIT_WIDTH} / ${FLIT_HEIGHT}` }}
		>
			{visible.map((index) => {
				const left = flitPipeX(index, state.distance);
				const middle = flitGap(state.seed, index);
				return (
					<div key={index}>
						<div
							className="absolute bg-emerald-500 ring-4 ring-emerald-700/50"
							style={{
								left: across(left),
								width: across(FLIT_PIPE_WIDTH),
								top: 0,
								height: down(middle - FLIT_GAP / 2),
							}}
						/>
						<div
							className="absolute bg-emerald-500 ring-4 ring-emerald-700/50"
							style={{
								left: across(left),
								width: across(FLIT_PIPE_WIDTH),
								top: down(middle + FLIT_GAP / 2),
								bottom: 0,
							}}
						/>
					</div>
				);
			})}

			<div
				data-testid="flit-bird"
				className="absolute flex items-center justify-center"
				style={{
					left: across(FLIT_BIRD_X - FLIT_BIRD_RADIUS),
					top: down(state.y - FLIT_BIRD_RADIUS),
					width: across(FLIT_BIRD_RADIUS * 2),
					height: down(FLIT_BIRD_RADIUS * 2),
					// Tips up when climbing and down when falling — the one bit of
					// polish that makes it read as flying rather than sliding.
					transform: `rotate(${Math.max(-30, Math.min(60, state.velocity * 2))}deg)`,
				}}
			>
				<span className="text-[min(6vw,2.5rem)] leading-none" aria-hidden="true">
					🐦
				</span>
			</div>
		</button>
	);
}

/** The one thing worth saying under a board that is entirely a button. */
export function FlitControls() {
	return (
		<p className="py-4 text-center text-lg font-semibold opacity-70">
			Tap the sky to flap — or press space
		</p>
	);
}

/**
 * Space and the up arrow, for whoever is playing on a laptop.
 *
 * The board itself is a button, so a keyboard player could tab to it and press
 * Enter — but this is a game about tapping quickly, and reaching for Enter is
 * not the same thing.
 */
function useTapAnywhere(onPress: (input: FlitInput) => void, live: boolean) {
	useEffect(() => {
		if (!live) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== " " && event.key !== "ArrowUp") return;
			// Space scrolls the page, and the page is the game.
			event.preventDefault();
			onPress({ flap: true });
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [onPress, live]);
}
